package com.castly.admin.ui.audiobooks

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.FormField
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.admin.ui.components.LabelSelector
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun RssAudiobookImportForm(
    apiClient: ApiClient,
    labels: List<LabelDto> = emptyList(),
    userLabelId: Int? = null,
    isLabelEditor: Boolean = false,
    onImportComplete: (String) -> Unit = {},
    onCancel: (() -> Unit)? = null
) {
    val colors = AppTheme.colors
    var rssUrl by remember { mutableStateOf("") }
    var selectedLabelId by remember { mutableStateOf<Int?>(if (isLabelEditor) userLabelId else null) }
    var isParsing by remember { mutableStateOf(false) }
    var isConfirming by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var preview by remember { mutableStateOf<RssImportPreview?>(null) }
    var importResult by remember { mutableStateOf<String?>(null) }

    // Override fields
    var isbnOverride by remember { mutableStateOf("") }
    var authorOverride by remember { mutableStateOf("") }
    var readerOverride by remember { mutableStateOf("") }
    var titleOverride by remember { mutableStateOf("") }

    val scope = rememberCoroutineScope()

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            themeTransition()
        }
    }) {
        H2({
            style {
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
                marginBottom(Spacing.lg)
            }
        }) { Text("Import from RSS Feed") }

        P({
            style {
                fontSize(Typography.base)
                color(Color(colors.textMuted))
                marginBottom(Spacing.xxl)
            }
        }) {
            Text("Import an audiobook from a podcast RSS feed. Each episode becomes a chapter. Audio files will be downloaded to S3 in the background.")
        }

        when {
            importResult != null -> {
                // Success
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#d1fae5" else "#052e16"))
                        border(1.px, LineStyle.Solid, Color(colors.success))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.lg)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.lg)
                            fontWeight("600")
                            color(Color(colors.success))
                            marginBottom(Spacing.sm)
                        }
                    }) { Text("Import Successful") }
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.text))
                        }
                    }) { Text("Audiobook created. Audio downloads have been queued in the background.") }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    Button({
                        onClick {
                            importResult = null
                            preview = null
                            rssUrl = ""
                            onCancel?.invoke()
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(Color(colors.primary))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) { Text("Close") }
                }
            }

            preview != null -> {
                // Preview
                RssPreviewView(preview = preview!!)

                // Label selector
                if (labels.isNotEmpty()) {
                    Div({
                        style { marginTop(Spacing.lg) }
                    }) {
                        LabelSelector(
                            labels = labels,
                            selectedLabelId = selectedLabelId,
                            onLabelSelected = { selectedLabelId = it },
                            readOnly = isLabelEditor,
                            userLabelId = userLabelId
                        )
                    }
                }

                // Override fields
                Div({
                    style {
                        marginTop(Spacing.lg)
                        padding(Spacing.lg)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.md)
                    }
                }) {
                    Div({
                        style {
                            fontSize(15.px)
                            fontWeight("600")
                            color(Color(colors.text))
                            marginBottom(Spacing.md)
                        }
                    }) { Text("Override Fields (optional)") }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(Spacing.md)
                        }
                    }) {
                        OverrideField("ISBN", isbnOverride, "e.g. 9780140449136") { isbnOverride = it }
                        OverrideField("Author", authorOverride, preview!!.author ?: "") { authorOverride = it }
                        OverrideField("Reader/Narrator", readerOverride, preview!!.narrator ?: "") { readerOverride = it }
                        OverrideField("Title", titleOverride, preview!!.title) { titleOverride = it }
                    }
                }

                errorMessage?.let { error ->
                    ErrorBanner(error)
                }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(Spacing.md)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    Button({
                        if (isConfirming) disabled()
                        onClick {
                            preview = null
                            errorMessage = null
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color(colors.text))
                            backgroundColor(Color(colors.surface))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) { Text("Back") }

                    Button({
                        if (isConfirming) disabled()
                        onClick {
                            isConfirming = true
                            errorMessage = null
                            scope.launch {
                                try {
                                    val request = RssImportConfirmRequest(
                                        rssUrl = rssUrl,
                                        isbn = isbnOverride.takeIf { it.isNotBlank() },
                                        authorName = authorOverride.takeIf { it.isNotBlank() },
                                        readerName = readerOverride.takeIf { it.isNotBlank() },
                                        titleOverride = titleOverride.takeIf { it.isNotBlank() },
                                        labelId = selectedLabelId
                                    )
                                    val response = apiClient.confirmRssAudiobookImport(request)
                                    val audiobookId = response["id"] ?: ""
                                    importResult = audiobookId
                                    onImportComplete(audiobookId)
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Import failed"
                                } finally {
                                    isConfirming = false
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(Color(colors.primary))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                        }
                    }) { Text(if (isConfirming) "Importing..." else "Confirm Import") }
                }
            }

            else -> {
                // URL input
                FormField("RSS Feed URL") {
                    Input(InputType.Url) {
                        value(rssUrl)
                        onInput { event -> rssUrl = event.value.trim() }
                        attr("placeholder", "https://example.com/feed.xml")
                        if (isParsing) disabled()
                        style {
                            width(100.percent)
                            padding(Spacing.sm, Spacing.md)
                            fontSize(Typography.base)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.md)
                            backgroundColor(Color(colors.surface))
                            color(Color(colors.text))
                            themeTransition()
                            property("box-sizing", "border-box")
                        }
                    }
                }

                if (isParsing) {
                    IndeterminateProgressBar("Fetching RSS feed...")
                }

                errorMessage?.let { error ->
                    ErrorBanner(error)
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(Spacing.md)
                        marginTop(Spacing.xxl)
                    }
                }) {
                    if (onCancel != null) {
                        Button({
                            if (isParsing) disabled()
                            onClick { if (!isParsing) onCancel() }
                            style {
                                padding(Spacing.sm, Spacing.lg)
                                fontSize(Typography.base)
                                fontWeight("500")
                                color(Color(colors.text))
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                cursor("pointer")
                            }
                        }) { Text("Cancel") }
                    }

                    Button({
                        val isValidUrl = rssUrl.startsWith("http://") || rssUrl.startsWith("https://")
                        if (!isValidUrl || isParsing) disabled()
                        onClick {
                            isParsing = true
                            errorMessage = null
                            scope.launch {
                                try {
                                    preview = apiClient.rssAudiobookPreview(rssUrl)
                                } catch (e: Exception) {
                                    errorMessage = e.message ?: "Failed to fetch RSS feed"
                                } finally {
                                    isParsing = false
                                }
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.lg)
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color("#ffffff"))
                            backgroundColor(
                                if (rssUrl.isBlank() || isParsing) Color(colors.textMuted)
                                else Color(colors.primary)
                            )
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor(if (rssUrl.isBlank() || isParsing) "not-allowed" else "pointer")
                        }
                    }) { Text(if (isParsing) "Fetching..." else "Preview Feed") }
                }
            }
        }
    }

    Style {
        """
        @keyframes indeterminate {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
        }
        """.trimIndent()
    }
}

@Composable
private fun RssPreviewView(preview: RssImportPreview) {
    val colors = AppTheme.colors

    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            padding(Spacing.lg)
            borderRadius(BorderRadius.md)
            marginBottom(Spacing.lg)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.lg)
            }
        }) {
            preview.coverImageUrl?.let { url ->
                Img(url) {
                    style {
                        width(80.px)
                        height(80.px)
                        borderRadius(BorderRadius.md)
                        property("object-fit", "cover")
                        flexShrink(0)
                    }
                }
            }

            Div({ style { flex("1") } }) {
                Div({
                    style {
                        fontWeight("600")
                        fontSize(Typography.lg)
                        color(Color(colors.text))
                        marginBottom(Spacing.sm)
                    }
                }) { Text(preview.title) }

                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(Spacing.xs)
                    }
                }) {
                    preview.author?.let { Div { Text("Author: $it") } }
                    preview.narrator?.let { Div { Text("Narrator: $it") } }
                    Div { Text("Chapters: ${preview.chapterCount}") }
                    preview.totalDurationSeconds?.let { secs ->
                        val hours = secs / 3600
                        val mins = (secs % 3600) / 60
                        Div { Text("Duration: ${hours}h ${mins}m") }
                    }
                    preview.language?.let { Div { Text("Language: $it") } }
                }
            }
        }

        // Chapter list
        if (preview.chapters.isNotEmpty()) {
            Div({
                style {
                    marginTop(Spacing.lg)
                    fontSize(15.px)
                    fontWeight("600")
                    color(Color(colors.text))
                    marginBottom(Spacing.sm)
                }
            }) { Text("Chapters (${preview.chapters.size})") }

            Div({
                style {
                    maxHeight(200.px)
                    overflowY("auto")
                }
            }) {
                preview.chapters.forEach { chapter ->
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            padding(Spacing.xs, Spacing.sm)
                            fontSize(13.px)
                            color(Color(colors.textMuted))
                            property("border-bottom", "1px solid ${colors.border}")
                        }
                    }) {
                        Span { Text("${chapter.number}. ${chapter.title}") }
                        chapter.durationSeconds?.let { secs ->
                            Span { Text("${secs / 60}:${(secs % 60).toString().padStart(2, '0')}") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun OverrideField(label: String, value: String, placeholder: String, onValueChange: (String) -> Unit) {
    val colors = AppTheme.colors
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(Spacing.md)
        }
    }) {
        Div({
            style {
                width(120.px)
                fontSize(Typography.base)
                fontWeight("500")
                color(Color(colors.text))
                flexShrink(0)
            }
        }) { Text(label) }

        Input(InputType.Text) {
            value(value)
            onInput { event -> onValueChange(event.value) }
            attr("placeholder", placeholder)
            style {
                flex("1")
                padding(6.px, Spacing.sm)
                fontSize(Typography.base)
                border(1.px, LineStyle.Solid, Color(colors.border))
                borderRadius(BorderRadius.sm)
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
            }
        }
    }
}
